import React, { useEffect, useState } from 'react';
import type { AssignmentCoverImage } from '../../types/assignmentPointsNote';
import {
  assignmentCoverCacheVersion,
  getCachedAssignmentCoverUrl,
  peekCachedAssignmentCoverUrl,
} from '../../utils/assignmentCoverImageCache';

type Props = {
  image: Pick<AssignmentCoverImage, 'id' | 'url' | 'path' | 'createdAt'>;
  alt?: string;
  className?: string;
  /** 캐시·원격 모두 실패 시 */
  fallback?: React.ReactNode;
  /** true면 메모리·IndexedDB만 사용하고 원격 URL/fetch를 쓰지 않음 */
  cacheOnly?: boolean;
};

/**
 * 과제 커버: IndexedDB/메모리 Blob URL 우선, 버전(path|createdAt) 변경 시 재수신.
 * 기본은 로딩 중 원격 URL로 먼저 표시. `cacheOnly`면 캐시 히트만 표시한다.
 */
const CachedAssignmentCoverImage: React.FC<Props> = ({
  image,
  alt = '',
  className,
  fallback = null,
  cacheOnly = false,
}) => {
  const version = assignmentCoverCacheVersion(image);
  const [src, setSrc] = useState<string | null>(
    () => peekCachedAssignmentCoverUrl(image) ?? (cacheOnly ? null : image.url.trim() || null)
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    const peeked = peekCachedAssignmentCoverUrl(image);
    setSrc(peeked ?? (cacheOnly ? null : image.url.trim() || null));
    void getCachedAssignmentCoverUrl(image, { allowNetwork: !cacheOnly }).then((url) => {
      if (cancelled) return;
      if (url) setSrc(url);
      else if (cacheOnly || !image.url.trim()) setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [image.id, image.url, image.path, image.createdAt, version, cacheOnly]);

  if (failed || !src) {
    return <>{fallback}</>;
  }

  return (
    <img
      key={`${image.id}:${version}`}
      className={className}
      src={src}
      alt={alt}
      draggable={false}
      onError={() => {
        if (!cacheOnly && src !== image.url && image.url.trim()) {
          setSrc(image.url);
          return;
        }
        setFailed(true);
      }}
    />
  );
};

export default CachedAssignmentCoverImage;
